import { AGGREGATION_OPTIONS, GROUP_BY_OPTIONS } from '../constants';
import { getGroupByBehaviour, GroupByBehaviour } from './behaviour';

/**
 * applyAverage behaviour matrix (aggregation | group_by):
 *
 *   sum     | any            → divides by athlete count (sum always reduces across athletes)
 *   count   | activity/period → divides by athlete count (via group-by map)
 *   count   | total          → divides by athlete count (via group-by map)
 *   count   | time/distance  → passthrough (per-unit rates, already normalised by backend)
 *   average | activity/period → divides by athlete count (via group-by map)
 *   average | total          → passthrough (backend pre-averaged; dividing again would double-average)
 *   average | time/distance  → passthrough (per-unit rates)
 *   max/min/ratio/pct_max/pct_average/percentage/na | any → passthrough (defaultStrategy)
 *
 */

export type StatTransformStrategy = {
    applyAverage(originalValue: number, uniqueAthleteCount: number, groupByOption: string): number;
    applyNormalisation(originalValue: number, multiplier: number, groupByOption: string): number;
};

export const getStatsTransformStrategy = (aggregation: string) => {
    return strategyMap[aggregation] || defaultStrategy;
};

const defaultStrategy: StatTransformStrategy = {
    applyAverage: (value) => value,
    applyNormalisation: (value) => value,
};

const delegateToGroupBy: StatTransformStrategy = {
    applyAverage: (value, athleteCount, groupByOption) => {
        const behaviour: GroupByBehaviour = getGroupByBehaviour(groupByOption);
        return behaviour.averaging(value, athleteCount);
    },
    applyNormalisation: (value, multiplier, groupByOption) => {
        const behaviour: GroupByBehaviour = getGroupByBehaviour(groupByOption);
        return behaviour.normalisation(value, multiplier);
    },
};

// Looks up and returns the desired functionality for the given aggregation and groupBy option
export const strategyMap: Record<string, StatTransformStrategy> = {
    [AGGREGATION_OPTIONS.count]: delegateToGroupBy,
    [AGGREGATION_OPTIONS.average]: {
        // Average params are pre-averaged by the backend. For group_by='total' they're already at
        // the final aggregate, so we must not divide by athlete count again (would double-average).
        // For activity/period, backend returns per-period averages that still need scaling + averaging.
        applyAverage: (value, athleteCount, groupByOption) =>
            groupByOption === GROUP_BY_OPTIONS.total
                ? value
                : delegateToGroupBy.applyAverage(value, athleteCount, groupByOption),
        applyNormalisation: delegateToGroupBy.applyNormalisation,
    },
    [AGGREGATION_OPTIONS.sum]: {
        applyAverage: (value, athleteCount) => value / (athleteCount || 1),
        applyNormalisation: (value) => value,
    },

    // Extend here if new aggregations are needed
};
